/**
 * Persist Control Centre fuel-transaction upload history across refreshes.
 * Skipped-row detail lives on the ingest response only — store it client-side
 * (same browser) so fleet managers can re-open the modal after a reload.
 * Storage is scoped by organisationId to avoid cross-tenant leakage.
 */

import { getOrganizationIdFromToken } from '../auth/cognitoAuth';
import type { EflexSkippedRow } from './fuelTransactionUpload';

export type FuelUploadHistoryStatus = 'parsed' | 'warn' | 'failed';

export type FuelUploadHistoryRow = {
  id: string;
  status: FuelUploadHistoryStatus;
  filename: string;
  periodLabel: string;
  rows: number | null;
  detail?: string;
  /** In-memory only — File handles cannot be serialised. */
  file?: File;
  skippedRows?: EflexSkippedRow[];
  skippedRowsTruncated?: boolean;
  skippedCount?: number;
  uploadedAt?: string;
};

type PersistedState = {
  history: FuelUploadHistoryRow[];
};

const STORAGE_KEY_PREFIX = 'zeefleet.fuel.transactionUploadHistory.v2';
/** Same key cognitoAuth uses for the ID token (custom:organisationId). */
const ID_TOKEN_KEY = 'zecure_cognito_id_token';
const MAX_ROWS = 40;

function resolveOrgId(explicit?: string | null): string | null {
  if (explicit && explicit.trim()) return explicit.trim();
  try {
    const token = localStorage.getItem(ID_TOKEN_KEY);
    if (!token) return null;
    return getOrganizationIdFromToken(token);
  } catch {
    return null;
  }
}

function storageKey(orgId: string): string {
  return `${STORAGE_KEY_PREFIX}:${orgId}`;
}

/** Strip File handles and any accidental unmasked long card numbers from candidates. */
function sanitizeSkippedRow(row: EflexSkippedRow): EflexSkippedRow {
  if (!row.candidate) return row;
  const card = row.candidate.cardNumber;
  if (!card || card.startsWith('****') || card.length <= 8) return row;
  return {
    ...row,
    candidate: {
      ...row.candidate,
      cardNumber: `****${card.replace(/\s+/g, '').slice(-4)}`,
    },
  };
}

function stripTransient(row: FuelUploadHistoryRow): FuelUploadHistoryRow {
  const { file: _file, ...rest } = row;
  return {
    ...rest,
    skippedRows: rest.skippedRows?.map(sanitizeSkippedRow),
  };
}

export function loadFuelUploadHistory(organisationId?: string | null): PersistedState {
  const orgId = resolveOrgId(organisationId);
  if (!orgId) return { history: [] };
  try {
    const raw = localStorage.getItem(storageKey(orgId));
    if (!raw) return { history: [] };
    const parsed = JSON.parse(raw) as PersistedState & { lastPeriod?: unknown };
    if (!parsed || !Array.isArray(parsed.history)) {
      return { history: [] };
    }
    return {
      history: parsed.history.map(stripTransient).slice(0, MAX_ROWS),
    };
  } catch {
    return { history: [] };
  }
}

export function saveFuelUploadHistory(
  state: PersistedState,
  organisationId?: string | null,
): void {
  const orgId = resolveOrgId(organisationId);
  if (!orgId) return;
  try {
    const payload: PersistedState = {
      history: state.history.map(stripTransient).slice(0, MAX_ROWS),
    };
    localStorage.setItem(storageKey(orgId), JSON.stringify(payload));
  } catch {
    // Quota / private mode — history stays in-memory for this page only.
  }
}

export function applyAcceptedSkipsToHistoryRow(
  row: FuelUploadHistoryRow,
  acceptedRows: number[],
): FuelUploadHistoryRow {
  const skipped = row.skippedRows ?? [];
  const acceptedSet = new Set(acceptedRows);
  const remaining = skipped.filter((s) => !acceptedSet.has(s.row));
  // Only count accepts that actually removed a row we were tracking, so a
  // duplicated or stale row number in `acceptedRows` cannot inflate the queued
  // total. For a truncated list we can't verify membership, so trust the caller.
  const acceptedCount = row.skippedRowsTruncated
    ? new Set(acceptedRows).size
    : skipped.length - remaining.length;
  // Truncated lists: keep residual total = prior skippedCount − accepted.
  // Non-truncated: count equals remaining detail rows.
  const nextSkipped = row.skippedRowsTruncated
    ? Math.max(0, (row.skippedCount ?? acceptedCount + remaining.length) - acceptedCount)
    : remaining.length;
  const nextQueued =
    typeof row.rows === 'number' ? row.rows + acceptedCount : acceptedCount;
  const stillWarn = nextSkipped > 0;
  const stillHaveDetails = remaining.length > 0;
  return {
    ...row,
    rows: nextQueued,
    skippedCount: nextSkipped,
    skippedRows: stillHaveDetails ? remaining : undefined,
    skippedRowsTruncated: stillHaveDetails ? Boolean(row.skippedRowsTruncated) : false,
    // Accepting overrides recovers an empty/failed upload into warn/parsed.
    status: stillWarn ? 'warn' : 'parsed',
    detail: stillWarn
      ? stillHaveDetails
        ? `${nextSkipped} rows skipped`
        : `${nextSkipped} rows skipped (details truncated)`
      : undefined,
  };
}
